import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles,
    deleteMaternityFile,
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const handleDeleteFile = (file: MaternityFile) => {
    if (window.confirm(`WARNING: This will permanently delete the maternity file for ${file.patient.firstName} ${file.patient.surname} (${file.bmcFileNumber}) and ALL of its records. This cannot be undone. Are you sure?`)) {
      deleteMaternityFile(file.id);
      setSelectedFile(null);
    }
  };
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-7151" data-mp="25vvo1bq4in5e">
      <div className="flex justify-between items-center print:hidden" data-id="element-7152" data-mp="20zoqo7bsmjz2">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-7153" data-mp="20xc0x7zsn5bg">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-7154" data-mp="29ysbbfqlz7fo">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-7155" data-mp="29ya45o2er8sz">
            <div className="relative max-w-md" data-id="element-7156" data-mp="2ony68rieybhr">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7157" data-mp="2oqu3seqcmy5o">
                <Search className="h-5 w-5 text-gray-400" data-id="element-7158" data-mp="2js0rgpmstd73" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7159" data-mp="2xommztz6ypyc" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-7160" data-mp="t9kfyxhiaslu">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-7161" data-mp="kemidd3eyro6">
                  <div className="flex items-center" data-id="element-7162" data-mp="5jzvga5pfnag">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-7163" data-mp="ah8d89mxxpb6">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-7164" data-mp="3smuv6wnp9zir" />
                    </div>
                    <div data-id="element-7165" data-mp="1gaarvs5hpup">
                      <p className="text-sm font-medium text-blue-600" data-id="element-7166" data-mp="1fid8fezti1s">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-7167" data-mp="1qvzw9vy75k7t">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-7168" data-mp="1tgpixmkac33g">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-7169" data-mp="1kgt2g881uajh">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-7170" data-mp="1fh6m3pwxce6g">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-7171" data-mp="1ydps46cfllys">
                      <FileText className="w-4 h-4 mr-2" data-id="element-7172" data-mp="2vtv7c60sdbnt" /> View File
                    </Button>
                    {isAdmin && <Button variant="danger" onClick={() => handleDeleteFile(file)} data-id="element-7173" data-mp="2r2dfc7gqqlez">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-7174" data-mp="33do0zmmf0au2" /> Delete
                      </Button>}
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-7175" data-mp="3dbtzdu90iyu6">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-7176" data-mp="3ddesrgjlcbqg" />
                <p className="text-lg font-medium text-gray-900" data-id="element-7177" data-mp="3he7187kn2clo">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-7178" data-mp="3r49v09zl3ul2">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-7179" data-mp="3w1js8bt7tajg">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-7180" data-mp="8v9aoeq5vb61">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-7181" data-mp="q7qlzq9eop3t">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-7182" data-mp="144nlsh7i5gwu">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-7183" data-mp="z8ouakqvnis7">
                  <Printer className="w-4 h-4 mr-1" data-id="element-7184" data-mp="bhjfm8woawjh" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-7185" data-mp="6izntrveeqj7">
                  <Download className="w-4 h-4 mr-1" data-id="element-7186" data-mp="gh5o651k3oly" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => handleDeleteFile(selectedFile)} data-id="element-7187" data-mp="3oumh9r7d1xik">
                    <Trash2 className="w-4 h-4 mr-1" data-id="element-7188" data-mp="1zeiqd14wz5eh" /> Delete
                  </Button>}
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-7189" data-mp="248n3610v27nh">
                  <X className="w-5 h-5 text-gray-500" data-id="element-7190" data-mp="1uhsdsnsbirxt" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-7191" data-mp="2i6usbmrno4o3">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-7192" data-mp="2966ht21h0mfy">
                <div className="flex items-center" data-id="element-7193" data-mp="2e485y1dr7bya">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-7194" data-mp="2e6ugm9vxy7kq" />
                  <div data-id="element-7195" data-mp="a4vzbll9huy4">
                    <h1 className="text-xl font-bold uppercase" data-id="element-7196" data-mp="a6p6d1zseh6n">
                      BMED MEDICAL CENTRE
                    </h1>
                    <p className="text-xs italic" data-id="element-7197" data-mp="e93o1irpzawc">Caring Healing Leading</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-7198" data-mp="5epaplqr2afv">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-7199" data-mp="5de99sks1erx">
                    Maternity Record
                  </h2>
                  <p data-id="element-7200" data-mp="3rmf5p1tttxau">
                    <span className="font-semibold" data-id="element-7201" data-mp="3rmyloczx2bm8">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-7202" data-mp="3doh4yz8fhzdt">
                    <span className="font-semibold" data-id="element-7203" data-mp="3ikxqrms5o1nq">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-7204" data-mp="1gtceflcu3gnv">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7205" data-mp="1qxsry82ucloi">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-7206" data-mp="1lzhgjswwe346">
                  <div data-id="element-7207" data-mp="1zq30lky38j3n">
                    <span className="font-semibold" data-id="element-7208" data-mp="1zqmhcyc051g3">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-7209" data-mp="1ux9wmzag1esm">
                    <span className="font-semibold" data-id="element-7210" data-mp="29oaf7x4wmp2h">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-7211" data-mp="24oea1ukh0rlc">
                    <span className="font-semibold" data-id="element-7212" data-mp="24oxqt7ydx9xs">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-7213" data-mp="2en3tvsvxgm48">
                    <span className="font-semibold" data-id="element-7214" data-mp="2eoomhcyoluzg">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-7215" data-mp="ocr2lqp0eupe">
                    <span className="font-semibold" data-id="element-7216" data-mp="k9ug3owcmeg6">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-7217" data-mp="2jfxaa1ncn542">
                    <span className="font-semibold" data-id="element-7218" data-mp="2jggphalm7fee">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-7219" data-mp="2tap7h4d0rpyb">
                    <span className="font-semibold" data-id="element-7220" data-mp="2yb3h2qu9r7yg">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-7221" data-mp="32avt5divfw8k">
                    <span className="font-semibold" data-id="element-7222" data-mp="37bjky1exch3u">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-7223" data-mp="3h131h0f5zyvj">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7224" data-mp="3m17jiuvq11nv">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-7225" data-mp="3q3kohh2n8vjl">
                  <div data-id="element-7226" data-mp="1eexrik835zsn">
                    <span className="font-semibold" data-id="element-7227" data-mp="1eg8skca5ctg2">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-7228" data-mp="1afyox69tuf4d">
                    <span className="font-semibold" data-id="element-7229" data-mp="15e9dvqmlu1ga">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-7230" data-mp="10gzebi5i49eq">
                    <span className="font-semibold" data-id="element-7231" data-mp="10hr9irv6s54h">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-7232" data-mp="1t54xw1ktjscz">
                    <span className="font-semibold" data-id="element-7233" data-mp="1t4loxad5ceb3">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-7234" data-mp="1jesgujc7qhbl">
                    <span className="font-semibold" data-id="element-7235" data-mp="1je0jp42ywbj7">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-7236" data-mp="2c0v5c4bitgy9">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7237" data-mp="1wketyd6mns6k">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-7238" data-mp="20nkxve69p79v">
                  <div data-id="element-7239" data-mp="20k6zsi4mzk88">
                    <span className="font-semibold" data-id="element-7240" data-mp="1mna6ma11pqo6">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-7241" data-mp="1rmdc1vkxtywo">
                    <span className="font-semibold" data-id="element-7242" data-mp="1rnohs0xy1gqf">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-7243" data-mp="25meg1jwi1mgd">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7244" data-mp="25lcqkaeqkxf4">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-7245" data-mp="2aid0wximcmc1">
                  <div data-id="element-7246" data-mp="2y98w84ma9zo1">
                    <span className="font-semibold" data-id="element-7247" data-mp="2y8z5getezoha">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-7248" data-mp="rjorfrmlxfnn">
                    <div data-id="element-7249" data-mp="mlvpjasc77t6">
                      <span className="font-semibold" data-id="element-7250" data-mp="mmomxl5eokyn">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-7251" data-mp="wc86yu4uukv3">
                      <span className="font-semibold" data-id="element-7252" data-mp="we1e0ajdr73m">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-7253" data-mp="3o2lu69w2tuq">
                      <span className="font-semibold" data-id="element-7254" data-mp="3ouhti7eetlj">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-7255" data-mp="hlhgpqiwvtu3">
                      <span className="font-semibold" data-id="element-7256" data-mp="co9568s9qo1t">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-7257" data-mp="1t4twtfxp7d93">
                    <div data-id="element-7258" data-mp="1o34hvp7cuffq">
                      <span className="font-semibold" data-id="element-7259" data-mp="1o3nxv0dg2tr4">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-7260" data-mp="qotaonjaqhbs">
                      <span className="font-semibold" data-id="element-7261" data-mp="qo1epblsehkz">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-7262" data-mp="zp7zhwvgyek1">
                    <span className="font-semibold" data-id="element-7263" data-mp="zpr8go355slx">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-7264" data-mp="70tx74xckw2o">
                    <span className="font-semibold" data-id="element-7265" data-mp="7252582j49vd">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-7266" data-mp="gthwc6bsvc4k">
                    <div data-id="element-7267" data-mp="3fgdpo1q72ago">
                      <span className="font-semibold" data-id="element-7268" data-mp="3fgvrrezjfiwi">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-7269" data-mp="3cxinxpfdnjp4">
                      <span className="font-semibold" data-id="element-7270" data-mp="3cvo4kfjr7zn5">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-7271" data-mp="32y9zfc3a6xa5">
                      <span className="font-semibold" data-id="element-7272" data-mp="32xrr3h3cgslv">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-7273" data-mp="2tz6cty4wnzqw">
                    <span className="font-semibold" data-id="element-7274" data-mp="2tzfu8xbvwykz">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-7275" data-mp="2k9d372obpupa">
                    <div data-id="element-7276" data-mp="2f98nvfz5j60n">
                      <span className="font-semibold" data-id="element-7277" data-mp="2fa06lq7nh99i">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-7278" data-mp="2b9gcywmacns4">
                      <span className="font-semibold" data-id="element-7279" data-mp="2680qd04gm97m">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-7280" data-mp="3u3fax70pg548">
                      <span className="font-semibold" data-id="element-7281" data-mp="2yp2uw2d2f8n">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-7282" data-mp="3l6mr84zlu60w">
                      <span className="font-semibold" data-id="element-7283" data-mp="3p5vkz9una3wg">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-7284" data-mp="3bcxkj93chlm1">
                    <div data-id="element-7285" data-mp="3g6ijb3crijma">
                      <span className="font-semibold" data-id="element-7286" data-mp="3g6s32977rujj">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-7287" data-mp="36fnohalgabp8">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-7288" data-mp="2shzcks9d2ewb">
                      <span className="font-semibold" data-id="element-7289" data-mp="2xeps9eh978h7">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-7290" data-mp="2dtpkh6ccusup">
                      <span className="font-semibold" data-id="element-7291" data-mp="2dv0on79pq2mc">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                  {(selectedFile.generalExam.notes || []).length > 0 && <div className="mt-2" data-id="element-7292" data-mp="2is0srcn04vat">
                      <span className="font-semibold" data-id="element-7293" data-mp="2iudappsrf5nv">Examination Notes:</span>
                      <ul className="mt-1 space-y-1" data-id="element-7294" data-mp="2wqh095soupu0">
                        {(selectedFile.generalExam.notes || []).map(note => <li key={note.id} data-id="element-7295" data-mp="36hv75ymyyrx6">
                            <span className="whitespace-pre-wrap" data-id="element-7296" data-mp="36gtmd2g5ir29">
                              {note.text}
                            </span>{' '}
                            <span className="text-xs italic" data-id="element-7297" data-mp="31l4jap4xas17">
                              — {note.recordedBy},{' '}
                              {new Date(note.recordedAt).toLocaleString()}
                            </span>
                          </li>)}
                      </ul>
                    </div>}
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-7298" data-mp="v8aapt5p56i">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7299" data-mp="u6widkra4l3">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-7300" data-mp="3rzgbzbb4j0gg">
                  <div className="flex space-x-8" data-id="element-7301" data-mp="34cqdul1wbq14">
                    <div data-id="element-7302" data-mp="349cfdnwcs0yy">
                      <span className="font-semibold" data-id="element-7303" data-mp="2zd408ai5o7rs">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-7304" data-mp="3e3l2tx6j13q9">
                      <span className="font-semibold" data-id="element-7305" data-mp="39aruz0frhf9y">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-7306" data-mp="2ljcttazq140a">
                      <span className="font-semibold" data-id="element-7307" data-mp="2gm45strw65r4">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-7308" data-mp="w3a8zwns4dh7">
                      <span className="font-semibold" data-id="element-7309" data-mp="w1z39rarwvng">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-7310" data-mp="n1aogudk2r9i">
                    <div data-id="element-7311" data-mp="r47dp3xlplmf">
                      <span className="font-semibold" data-id="element-7312" data-mp="r5idytru8b8s">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-7313" data-mp="15tvd1zm82a7i">
                      <span className="font-semibold" data-id="element-7314" data-mp="15uoag9zajncz">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-7315" data-mp="3qjuj23jpi97h">
                    <div data-id="element-7316" data-mp="3qm6xw7u643gb">
                      <span className="font-semibold" data-id="element-7317" data-mp="3vixdku228x17">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-7318" data-mp="1w56uiftb0gv7">
                      <span className="font-semibold" data-id="element-7319" data-mp="1w5ys1w6gooo4">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-7320" data-mp="1naskarnmrwiy">
                      <span className="font-semibold" data-id="element-7321" data-mp="1n9hgwsy3kqsd">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-7322" data-mp="1dfh4s74eela5">
                    <div data-id="element-7323" data-mp="18djewu1hss88">
                      <span className="font-semibold" data-id="element-7324" data-mp="18etbfesp5scr">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-7325" data-mp="32pzqmad24ykh">
                      <span className="font-semibold" data-id="element-7326" data-mp="2yq7gvubxgmdj">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-7327" data-mp="phsy3oanwcpp">
                    <span className="font-semibold" data-id="element-7328" data-mp="ph0z03huvwuo">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-7329" data-mp="ya4kmz2nng0e">
                    <span className="font-semibold" data-id="element-7330" data-mp="y9b9zn0ks6cz">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7331" data-mp="al1u11q1mctr">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-7332" data-mp="amwdeblo1wvq">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-7333" data-mp="1m0qhrvb4ffvm">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-7334" data-mp="1qx76orq59y9r">
                  <thead data-id="element-7335" data-mp="1pi4qmm9it57y">
                    <tr className="bg-gray-100" data-id="element-7336" data-mp="1kirutc0o8nt8">
                      <th className="border border-black p-1" data-id="element-7337" data-mp="1kk376086n3vy">Date</th>
                      <th className="border border-black p-1" data-id="element-7338" data-mp="1yj2n8jhmqagi">Time</th>
                      <th className="border border-black p-1" data-id="element-7339" data-mp="1yg84cwe9kxp7">Pulse</th>
                      <th className="border border-black p-1" data-id="element-7340" data-mp="1ufo3piuhfbyb">BP</th>
                      <th className="border border-black p-1" data-id="element-7341" data-mp="28fp5r1mhgpfd">FH</th>
                      <th className="border border-black p-1" data-id="element-7342" data-mp="23hxdg6hxf1an">Temp</th>
                      <th className="border border-black p-1" data-id="element-7343" data-mp="23fjjk0yzfv4o">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-7344" data-mp="2i60m5nncsr35">Contractions</th>
                      <th className="border border-black p-1" data-id="element-7345" data-mp="2d8i8xl2azppc">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-7346" data-mp="2r7hx6rlwm2kz">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-7347" data-mp="35txevt59lewr">Remarks</th>
                      <th className="border border-black p-1" data-id="element-7348" data-mp="35rufwjm4wbfw">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-7349" data-mp="2wt93753cfqn1">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-7350" data-mp="30t12er6ukkfq">
                          <td className="border border-black p-1" data-id="element-7351" data-mp="2n3phlro8xioh">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-7352" data-mp="2ruxqmmlnz214">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-7353" data-mp="2d3f2u2mkc377">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-7354" data-mp="244bjcxjhhls6">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-7355" data-mp="293eqqomxs43b">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-7356" data-mp="295hef20m9l4v">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-7357" data-mp="9w7toicpe6t3">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-7358" data-mp="4xwhi0yxrk7p">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-7359" data-mp="xm25cwladpbr">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-7360" data-mp="snqviluzretj">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-7361" data-mp="jmsv0djrhpfp">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-7362" data-mp="39dfowua6hp5o">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-7363" data-mp="1jsmnkjylatx8">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-7364" data-mp="1otkepm1kuyav">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-7365" data-mp="27j8v8a4omc6p">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7366" data-mp="27mejxo9efc1w">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-7367" data-mp="2gli2mr4nlpfv">
                  <div data-id="element-7368" data-mp="2ginkx7d0yiq5">
                    <span className="font-semibold" data-id="element-7369" data-mp="i5nawk55h2q6">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-7370" data-mp="m7qmr9vqe9bz">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-7371" data-mp="38dp4zkgu78p">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-7372" data-mp="c3jxa577l1ee">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-7373" data-mp="81ynispz138f">
                    <span className="font-semibold" data-id="element-7374" data-mp="83t1fn2trv30">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-7375" data-mp="3bj74lacvjz23">
                    <span className="font-semibold" data-id="element-7376" data-mp="3bk03jp5ldk9o">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-7377" data-mp="3kic4ol4340gd">
                    <span className="font-semibold" data-id="element-7378" data-mp="3khsnx7q67i3x">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-7379" data-mp="14tskwckike0d">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-7380" data-mp="189difaxcuuyd">
                    <span className="font-semibold" data-id="element-7381" data-mp="188278q1kykx8">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-7382" data-mp="ybpgo11c0n6p">
                    <span className="font-semibold" data-id="element-7383" data-mp="ych8r1vq02s8">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-7384" data-mp="1r04p24pyk792">
                    <span className="font-semibold" data-id="element-7385" data-mp="1qzuz2h4wy03d">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-7386" data-mp="1w1ai63nj5wj4">
                    <span className="font-semibold" data-id="element-7387" data-mp="1hal5sxyc2bdo">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-7388" data-mp="29yh0jx4cdt7t">
                    <div data-id="element-7389" data-mp="29x5x5yet6nh8">
                      <span className="font-semibold" data-id="element-7390" data-mp="2epfmpdc0hliz">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-7391" data-mp="3gnlu6i4gkrpo">
                      <span className="font-semibold" data-id="element-7392" data-mp="3lojep1cxxxuc">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-7393" data-mp="3vdksckw0k355">
                      <span className="font-semibold" data-id="element-7394" data-mp="3ve2ufy5cxbkz">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-7395" data-mp="32pf61tpba637">
                      <span className="font-semibold" data-id="element-7396" data-mp="32qqa7umo5fuu">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-7397" data-mp="3bqvgg1g4m87f">
                    <span className="font-semibold" data-id="element-7398" data-mp="3bs6m66t4tq16">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-7399" data-mp="r04jizzzozni">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-7400" data-mp="2bkjcts9z8kxx">
                    <span className="font-semibold" data-id="element-7401" data-mp="27gto92tay3cw">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-7402" data-mp="22oaekgxqjsdd">
                    <span className="font-semibold" data-id="element-7403" data-mp="22nsbp1gkifwh">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-7404" data-mp="1nsuf1wedz49l">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-7405" data-mp="1jrso02umhyip">
                    <span className="font-semibold" data-id="element-7406" data-mp="1jt3sy5zt1cbe">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-7407" data-mp="3e2xncuyofos8">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-7408" data-mp="3sa0qrd1so4nf">
                    <span className="font-semibold" data-id="element-7409" data-mp="w4ocsa7pccs">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-7410" data-mp="3o8yy5f2duquf">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-7411" data-mp="frav5hh4tf76">
                    <span className="font-semibold" data-id="element-7412" data-mp="jv0f1tmv38lv">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-7413" data-mp="atsu0d9cw49q">
                    <span className="font-semibold" data-id="element-7414" data-mp="at0zl5rhwcl1">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-7415" data-mp="2vk9vhbloidgk">
                    <span className="font-semibold" data-id="element-7416" data-mp="2vlkxb5vkdb51">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-7417" data-mp="2o34ykop50m3e">
                    <span className="font-semibold" data-id="element-7418" data-mp="2j2qoz27w1439">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-7419" data-mp="2s2do1hd59xt5">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-7420" data-mp="20bg1yw5a9bgn">
                    <span className="font-semibold" data-id="element-7421" data-mp="20cr1ukvlxz2h">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-7422" data-mp="29bvytmritkcj">
                  <h4 className="font-bold text-sm mb-1" data-id="element-7423" data-mp="29atbtpxbk21w">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-7424" data-mp="1ll8pcx1yimnv">
                    <thead data-id="element-7425" data-mp="1gmxhgrv82m8a">
                      <tr className="bg-gray-100" data-id="element-7426" data-mp="1glme2t5ovghp">
                        <th className="border border-black p-1" data-id="element-7427" data-mp="1ub8p16vmvydh">DATE</th>
                        <th className="border border-black p-1" data-id="element-7428" data-mp="1z90p4o6fmqsr">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-7429" data-mp="247lgrz7mc25l">SEX</th>
                        <th className="border border-black p-1" data-id="element-7430" data-mp="28zlnqbm3vvbo">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-7431" data-mp="2d0p2eusf3lbb">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-7432" data-mp="2i1ut6n2ioyi7">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-7433" data-mp="2rx6bffd4wnup">
                            <td className="border border-black p-1" data-id="element-7434" data-mp="sldc3c2vj6et">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-7435" data-mp="xkggcub158lq">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-7436" data-mp="363q3x0p0rfld">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-7437" data-mp="322o0e7o2khdl">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-7438" data-mp="3tt297ph873ie">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-7439" data-mp="3krkvgdrajdwv">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-7440" data-mp="3kqj9vfcnf90w">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7441" data-mp="2auw7odh8nzhr">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7442" data-mp="1wulhzdro0qy6">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-7443" data-mp="21swr1ma4yxfc">
                  <div data-id="element-7444" data-mp="1n4s76ezckfuz">
                    <span className="font-semibold" data-id="element-7445" data-mp="1n4a5v3xtvbg7">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-7446" data-mp="1rwtinyooy2jy">
                    <span className="font-semibold" data-id="element-7447" data-mp="1e43ubnlowljo">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-7448" data-mp="1i6ics6lg5jaf">
                    <span className="font-semibold" data-id="element-7449" data-mp="1i3mdoexv4oke">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-7450" data-mp="193pjxywm8zih">
                    <span className="font-semibold" data-id="element-7451" data-mp="19495dplh6619">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-7452" data-mp="zhevlnz9icec">
                    <div data-id="element-7453" data-mp="zipyzmospi4x">
                      <span className="font-semibold" data-id="element-7454" data-mp="zf270rz013sb">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-7455" data-mp="18gibkj34ubom">
                      <span className="font-semibold" data-id="element-7456" data-mp="18fqjhi8quw33">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-7457" data-mp="1i7d4256z27ii">
                      <span className="font-semibold" data-id="element-7458" data-mp="1i9qu1zmsotj7">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-7459" data-mp="1r688bbqgh7j6">
                    <span className="font-semibold" data-id="element-7460" data-mp="1n797o3brbwxj">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-7461" data-mp="2to1jfguy9kk2">
                    <span className="font-semibold" data-id="element-7462" data-mp="2yl1s7zj6p1ev">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-7463" data-mp="37kos2gw9lz5t">
                    <span className="font-semibold" data-id="element-7464" data-mp="37jlyu2bgzkmq">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-7465" data-mp="2fusdxra4hj6t">
                    <span className="font-semibold" data-id="element-7466" data-mp="2fs69i0ij3jst">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-7467" data-mp="2oof3v0rnfs83">
                    <span className="font-semibold" data-id="element-7468" data-mp="2ooowyx7zqfi0">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-7469" data-mp="1w58gfczhjxe3">
                    <span className="font-semibold" data-id="element-7470" data-mp="1on1vzm4qrr2b">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-7471" data-mp="1fl0zwsl8vb28">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-7472" data-mp="1fo6tak0woz3r">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-7473" data-mp="15opkhtqcy53k">
                    <div data-id="element-7474" data-mp="10xg0b8nkvtz2">
                      <span className="font-semibold" data-id="element-7475" data-mp="10wxy7ve8ilj8">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-7476" data-mp="rwris3yz7bhf">
                      <span className="font-semibold" data-id="element-7477" data-mp="rxklmtutdgua">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-7478" data-mp="hy222eitfx2r">
                      <span className="font-semibold" data-id="element-7479" data-mp="hzn04e4c9y5d">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-7480" data-mp="3hfw4a897w41s">
                      <span className="font-semibold" data-id="element-7481" data-mp="3hfnsyksqt6sp">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-7482" data-mp="37ii31fvv7lu2">
                      <span className="font-semibold" data-id="element-7483" data-mp="37jb17sgrd30l">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-7484" data-mp="41sujocq221r">
                      <span className="font-semibold" data-id="element-7485" data-mp="42koyvul1tqg">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-7486" data-mp="3v9x83gf10h90">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-7487" data-mp="2jtgp5d49jihe">
                      <span className="font-semibold" data-id="element-7488" data-mp="2jqkq1lgoinrd">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-7489" data-mp="ucbfrxih11lk">
                      <span className="font-semibold" data-id="element-7490" data-mp="ud4ov54wk36v">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-7491" data-mp="1497mpysq1fo0">
                      <span className="font-semibold" data-id="element-7492" data-mp="148xxidfi3cjd">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-7493" data-mp="1dzsvug60u2l6">
                      <span className="font-semibold" data-id="element-7494" data-mp="1e0arpboruesk">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-7495" data-mp="1n07ipjymvpqs">
                      <span className="font-semibold" data-id="element-7496" data-mp="1mzxrxu5rlek1">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-7497" data-mp="1rwxy052m6hb5">
                      <span className="font-semibold" data-id="element-7498" data-mp="3os3sao18qpix">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-7499" data-mp="3tqx6yjs9kmko">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-7500" data-mp="3hp5opwprlooe">
                      <span className="font-semibold" data-id="element-7501" data-mp="3hpymw9anr5ux">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-7502" data-mp="3qpc11f9q8ilz">
                      <span className="font-semibold" data-id="element-7503" data-mp="3qo0s711fcko0">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-7504" data-mp="4i3x8heggjs2">
                      <span className="font-semibold" data-id="element-7505" data-mp="4ind7skjoy3g">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-7506" data-mp="da5yuj09g63x">
                      <span className="font-semibold" data-id="element-7507" data-mp="d9w46i49tarw">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-7508" data-mp="n8ln8e7wl19q">
                      <span className="font-semibold" data-id="element-7509" data-mp="n94wz7negjco">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-7510" data-mp="1avqp1m75wfje">
                      <span className="font-semibold" data-id="element-7511" data-mp="1aw0f19s7imp3">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-7512" data-mp="11usgcqls3juh">
                      <span className="font-semibold" data-id="element-7513" data-mp="11thbsokie82b">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-7514" data-mp="rwmiql708zvf">
                      <span className="font-semibold" data-id="element-7515" data-mp="ryz83jf3ecii">Reflexes:</span>
                      <div data-id="element-7516" data-mp="e6rajpfzihir">
                        <span className="font-semibold" data-id="element-7517" data-mp="e3dbor6j4qg2">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-7518" data-mp="48levpkru7mc">
                        <span className="font-semibold" data-id="element-7519" data-mp="49d66o7c5j6t">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-7520" data-mp="1cghh5yeyc0c2">
                        <span className="font-semibold" data-id="element-7521" data-mp="17i7l7llekvpe">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-7522" data-mp="xrd0wmrpw66l">
                  <span className="font-semibold" data-id="element-7523" data-mp="xqa5c1jg9fkc">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-7524" data-mp="suutff505tqk">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7525" data-mp="ets6z1a3ihf4">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7526" data-mp="evv7wgcsduym">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-7527" data-mp="2979yt0hja0fy">
                  <thead data-id="element-7528" data-mp="2grubksfkp4vl">
                    <tr className="bg-gray-100" data-id="element-7529" data-mp="2gqj7eri7tv3y">
                      <th className="border border-black p-1" colSpan={2} data-id="element-7530" data-mp="2lpvwliwk7e9p">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-7531" data-mp="2qhx3sqeveesv">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-7532" data-mp="2vi1kohjoxbjm">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-7533" data-mp="22r1eq11stxpb">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-7534" data-mp="27ot9d2ttvxx7">
                      <th className="border border-black p-1" data-id="element-7535" data-mp="2cq0e0t0ocgzn">DATE</th>
                      <th className="border border-black p-1" data-id="element-7536" data-mp="2cn4gh5sqnubq">TIME</th>
                      <th className="border border-black p-1" data-id="element-7537" data-mp="3j51ar0xix37a">BP</th>
                      <th className="border border-black p-1" data-id="element-7538" data-mp="3o3upsxsgl29k">PULSE</th>
                      <th className="border border-black p-1" data-id="element-7539" data-mp="1o2edllmi8ewm">RESPS</th>
                      <th className="border border-black p-1" data-id="element-7540" data-mp="1o4hex1t3xugn">TEMP</th>
                      <th className="border border-black p-1" data-id="element-7541" data-mp="1j2i97l9walgj">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-7542" data-mp="1x3clif02d6kh">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-7543" data-mp="1t1hk7ai4ut6p">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-7544" data-mp="1szd7c329ajua">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-7545" data-mp="15con3apryfai">TEMP</th>
                      <th className="border border-black p-1" data-id="element-7546" data-mp="10dbxii7iqu48">RESPS</th>
                      <th className="border border-black p-1" data-id="element-7547" data-mp="10azavrqtfvku">CORD</th>
                      <th className="border border-black p-1" data-id="element-7548" data-mp="1e5i3xckvdmqh">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-7549" data-mp="19coysnlhocdv">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-7550" data-mp="lltaw1k5m1bw">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-7551" data-mp="hlj80xrnrr19">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-7552" data-mp="7h2sk5i3cbxz">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-7553" data-mp="3tqww4gu0hlzk">
                          <td className="border border-black p-1" data-id="element-7554" data-mp="2jao6c09excw">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-7555" data-mp="q8lq3wtcixsm">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-7556" data-mp="v76t23zzfvkr">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-7557" data-mp="g9wa69l0fnf0">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-7558" data-mp="lblnxwzmaf6s">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-7559" data-mp="3609ntug6ouch">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-7560" data-mp="2nas1glzmjth7">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-7561" data-mp="2db148cjoj0df">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-7562" data-mp="29b0q6t0hsr6w">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-7563" data-mp="361t1ldchdv2c">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-7564" data-mp="2w9djmg16p6hg">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-7565" data-mp="2s6h4x0erluis">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-7566" data-mp="3jz7tcoq7iexh">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-7567" data-mp="16io86feocpyx">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-7568" data-mp="1fhrtlq1bdhgl">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-7569" data-mp="1kifsexvsb2ld">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-7570" data-mp="1uesm3vrbxgg4">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-7571" data-mp="1z6alwghm9azi">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-7572" data-mp="284cimjubhhgz">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-7573" data-mp="2d5abbqcydtwq">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7574" data-mp="39et5jyxbk2ft">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-7575" data-mp="39gxkdbwraluv">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-7576" data-mp="51e741yu1moz">
                  <div data-id="element-7577" data-mp="52pai0od8sfk">
                    <span className="font-semibold" data-id="element-7578" data-mp="3nfdqb0mso8by">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-7579" data-mp="jxlsh5lz8u5u">
                    <span className="font-semibold" data-id="element-7580" data-mp="jx3vu7vekdxe">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-7581" data-mp="oqoorli52hpq">
                    <span className="font-semibold" data-id="element-7582" data-mp="y3fa9xzfhuba">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-7583" data-mp="u1kbb04yzt0o">
                    <span className="font-semibold" data-id="element-7584" data-mp="u099h5v34vc7">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-7585" data-mp="12uxdytsu68zy">
                  <thead data-id="element-7586" data-mp="f5332hs2uuic">
                    <tr className="bg-gray-100" data-id="element-7587" data-mp="f6mh01q9gtgy">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-7588" data-mp="b49cfgn931lr">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-7589" data-mp="p0wcvteew9wf">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-7590" data-mp="20hmmlmi1njus">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-7591" data-mp="1vijgdyqbv7l8">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-7592" data-mp="27lcfx80fro7o">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-7593" data-mp="2clqnyq21ey5p">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-7594" data-mp="2hh5vz1dd5zu7">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-7595" data-mp="1ovwbwapjldmi">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-7596" data-mp="1truv7jw0k5g2">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-7597" data-mp="1148t6g2fp8vb">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-7598" data-mp="163bo37op9ypk">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-7599" data-mp="jnldhg1py1ro">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-7600" data-mp="flzudcylcr91">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-7601" data-mp="at6nojjkb8ub">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-7602" data-mp="gmjlwq6c7k8l">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-7603" data-mp="7tonafbdkmrs">
                          <td className="border border-black p-1" data-id="element-7604" data-mp="7sn9w46vzo6w">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-7605" data-mp="3u1etkrjwjw22">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-7606" data-mp="2x7zxnc02cfr">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-7607" data-mp="3p2v5akhu686j">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-7608" data-mp="1jay6d0qldcsk">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-7609" data-mp="37ukgb26p5u41">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-7610" data-mp="3crkkf7jzkmsi">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-7611" data-mp="263njqeiuv7c1">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-7612" data-mp="216x2hnvbe5p1">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-7613" data-mp="a0sgcgx8co52">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-7614" data-mp="j1yzyyv6ef2j">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-7615" data-mp="j3a4j0wg3qup">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-7616" data-mp="12pg7uu8ko9r2">
                  <div data-id="element-7617" data-mp="12rsliv7arxyb">
                    <span className="font-semibold" data-id="element-7618" data-mp="16rkz5mbjsuaj">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-7619" data-mp="1bqxku3qonvbj">
                    <span className="font-semibold" data-id="element-7620" data-mp="1bobbpzo597r7">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7621" data-mp="2n78lezlim986">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-7622" data-mp="2n9larxtlrlv9">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-7623" data-mp="3ezg0vwpabfkz">
                  <thead data-id="element-7624" data-mp="3axl3326kbkby">
                    <tr className="bg-gray-100" data-id="element-7625" data-mp="3aye19ergh1ih">
                      <th className="border border-black p-2 text-left w-32" data-id="element-7626" data-mp="3azp51ekwi99l">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-7627" data-mp="3181k7wiunqh3">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-7628" data-mp="2ib1rwahyj58">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-7629" data-mp="327kuiuy71i3v">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-7630" data-mp="2sapyqjtb1vta">
                          <td className="border border-black p-2" data-id="element-7631" data-mp="2sbr8mkyl4c9f">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-7632" data-mp="2x76lb9kuw249">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-7633" data-mp="3pwvr008igqc7">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-7634" data-mp="3fy7lg0xpq3pe">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-7635" data-mp="20ja4q4pf56t1">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7636" data-mp="26z8yffg750dg">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-7637" data-mp="26x66uqzeaz6m">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-7638" data-mp="1ebwhbkst1krj">
                  <div data-id="element-7639" data-mp="19cjmacrs57dv">
                    <span className="font-semibold" data-id="element-7640" data-mp="19d331q5p1pqb">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-7641" data-mp="1j9y4ohx9k68t">
                    <span className="font-semibold" data-id="element-7642" data-mp="1j9oasj93lexu">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-7643" data-mp="vi92m9un43eo">
                    <span className="font-semibold" data-id="element-7644" data-mp="qlip9ua7zlwy">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-7645" data-mp="23uutindmq6a1">
                    <span className="font-semibold" data-id="element-7646" data-mp="23rgtvmwcob6a">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-7647" data-mp="2csx33rbfi78x">
                    <span className="font-semibold" data-id="element-7648" data-mp="2csoq7zfb31xq">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-7649" data-mp="2miq45ydy33yx">
                    <span className="font-semibold" data-id="element-7650" data-mp="2mj9plp2t0ahp">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7651" data-mp="2wfupxx3voxf7">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-7652" data-mp="124ps59fh3f7s">
                  <div className="font-bold uppercase" data-id="element-7653" data-mp="rzr90zgwyjmk">Mother</div>
                  <div className="font-bold uppercase" data-id="element-7654" data-mp="w1l50ug5yzge">Infant</div>
                  <div data-id="element-7655" data-mp="w0t9tkqhb3qn">
                    <span className="font-semibold" data-id="element-7656" data-mp="w3fkht8o1zd3">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-7657" data-mp="n7zrtfjndq93">
                    <span className="font-semibold" data-id="element-7658" data-mp="n7g6dousgjqb">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-7659" data-mp="dbd84u32558n">
                    <span className="font-semibold" data-id="element-7660" data-mp="dbwinpqdorcn">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-7661" data-mp="4a5dqn3nwoju">
                    <span className="font-semibold" data-id="element-7662" data-mp="4bgcg8i93643">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-7663" data-mp="1tnil6l72msp6">
                    <span className="font-semibold" data-id="element-7664" data-mp="1yozjc8y68122">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-7665" data-mp="1wb413mu2bc2x">
                    <span className="font-semibold" data-id="element-7666" data-mp="1s87hptwp7bxx">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-7667" data-mp="1n9w73gykwxen">
                    <span className="font-semibold" data-id="element-7668" data-mp="1ia03hitsn7zm">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-7669" data-mp="2fx8u1s8nj78h">
                    <span className="font-semibold" data-id="element-7670" data-mp="2ayxj1e6meqoo">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-7671" data-mp="265w0mw77g90c">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-7672" data-mp="2182x6axadt3r">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-7673" data-mp="2yq2yvtck9iyz">
                    <span className="font-semibold" data-id="element-7674" data-mp="2tx9r0wlspuio">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-7675" data-mp="366zgyitf5uyb">
                  <h5 className="font-bold uppercase mb-2" data-id="element-7676" data-mp="3b6vmwnlofwgi">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-7677" data-mp="3b5kj4ns8eope">
                    <div data-id="element-7678" data-mp="3g701g830yh43">
                      <span className="font-semibold" data-id="element-7679" data-mp="3g79wwb6u9gh6">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-7680" data-mp="3p5vart1d87bm">
                      <span className="font-semibold" data-id="element-7681" data-mp="3p52bte8nem41">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-7682" data-mp="2rlmrl1cl1bd">
                      <span className="font-semibold" data-id="element-7683" data-mp="2pr2m8xwhd8c">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-7684" data-mp="br8qidhjuj7l">
                      <span className="font-semibold" data-id="element-7685" data-mp="bss4fxfqgi67">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-7686" data-mp="14fftijazgenv">
                      <span className="font-semibold" data-id="element-7687" data-mp="1i8p1qgn72u4t">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-7688" data-mp="1e850p1zi36de">
                      <span className="font-semibold" data-id="element-7689" data-mp="qhrbdj8avlmi">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-7690" data-mp="lkio548aoree">
                      <span className="font-semibold" data-id="element-7691" data-mp="zifpedxp1lze">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-7692" data-mp="ui1dulwvvtwm">
                      <span className="font-semibold" data-id="element-7693" data-mp="ulp5tgmok898">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-7694" data-mp="25xzrz46x564p">
                      <span className="font-semibold" data-id="element-7695" data-mp="260vqatmohwto">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-7696" data-mp="2ihwrlgvtxhmx">
                      <span className="font-semibold" data-id="element-7697" data-mp="2if0utvvpwz02">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-7698" data-mp="29gyuzjnq0ced">
                      <span className="font-semibold" data-id="element-7699" data-mp="2x69n6cr08ygw">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-7700" data-mp="326xez0n25jc6">
                      <span className="font-semibold" data-id="element-7701" data-mp="2nf6sptskzhql">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-7702" data-mp="3g55gllv1h8to">
                      <span className="font-semibold" data-id="element-7703" data-mp="3g3uerrl5mb57">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-7704" data-mp="3ur2laf3ipsau">
                  <h5 className="font-bold uppercase mb-2" data-id="element-7705" data-mp="3ps857kwxqhuf">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-7706" data-mp="3kteo7iifw8pi">
                    <div data-id="element-7707" data-mp="3krljw9vau0ko">
                      <span className="font-semibold" data-id="element-7708" data-mp="3grtgebkrikm6">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-7709" data-mp="3bu1fis253o5u">
                      <span className="font-semibold" data-id="element-7710" data-mp="372057boj87ig">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-7711" data-mp="320udnh6lyqai">
                      <span className="font-semibold" data-id="element-7712" data-mp="2x43xyuyptwpm">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-7713" data-mp="2t4bkc3ugt0de">
                      <span className="font-semibold" data-id="element-7714" data-mp="2o35tycoa1p71">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-7715" data-mp="13ktqo4ffzx1c">
                      <span className="font-semibold" data-id="element-7716" data-mp="18jevkh5n34w4">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-7717" data-mp="1hk38t9n7l17y">
                      <span className="font-semibold" data-id="element-7718" data-mp="kulwibtq0gxa">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-7719" data-mp="owgvh9o6ii7w">
                      <span className="font-semibold" data-id="element-7720" data-mp="oturtl4esmuy">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-7721" data-mp="ynbm0q8gk3z5">
                      <span className="font-semibold" data-id="element-7722" data-mp="16mjeubooxop">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-7723" data-mp="63mtdgbnmkl3">
                      <span className="font-semibold" data-id="element-7724" data-mp="2vnj69f5w70k0">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-7725" data-mp="2qqj1d7ks43uh">
                      <span className="font-semibold" data-id="element-7726" data-mp="34ohgif6xc4b1">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-7727" data-mp="30lb0wgxx90rw">
                      <span className="font-semibold" data-id="element-7728" data-mp="30p8mrabvw6fh">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-7729" data-mp="39n0zq7u4v1zp">
                      <span className="font-semibold" data-id="element-7730" data-mp="39lpwc94lnw94">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-7731" data-mp="3jeomvwjnpwvd">
                      <span className="font-semibold" data-id="element-7732" data-mp="17qu4pr1ben6">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-7733" data-mp="ftxyjyad1jn8">
                  <div data-id="element-7734" data-mp="fv93i1fjkxfx">
                    <span className="font-semibold" data-id="element-7735" data-mp="ktius0y5bdw9">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-7736" data-mp="autlgwkbf1rm">
                    <span className="font-semibold" data-id="element-7737" data-mp="au1r1p2gfa2x">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-7738" data-mp="1wrg64tjtmyj">
                    <span className="font-semibold" data-id="element-7739" data-mp="3jb69dy6o7j8g">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7740" data-mp="3absz53aq2qmo">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-7741" data-mp="3f9uizqfz2tz7">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-7742" data-mp="229ctxfrb3em8">
                  <span className="font-semibold" data-id="element-7743" data-mp="268lqesdqdqlh">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7744" data-mp="2b6wyoyodnt1l">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-7745" data-mp="2g4yi5kpptudl">
                  <div data-id="element-7746" data-mp="2l65qbkvrsvks">
                    <span className="font-semibold" data-id="element-7747" data-mp="2p7zn3i2uhffo">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-7748" data-mp="2u0sr23qhojqp">
                    <span className="font-semibold" data-id="element-7749" data-mp="2yxj7is67hhcn">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-7750" data-mp="33ygl0rg9tn7t">
                    <span className="font-semibold" data-id="element-7751" data-mp="33v2ldqyzrs42">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-7752" data-mp="x9em82t26vsj">
                    <span className="font-semibold" data-id="element-7753" data-mp="x6k2kdhvdf06">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-7754" data-mp="n9nto6o26qv3">
                    <span className="font-semibold" data-id="element-7755" data-mp="naz76y7b3cze">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-7756" data-mp="1fznzc7qdw7z0">
                    <span className="font-semibold" data-id="element-7757" data-mp="1fy356j7zer1o">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-7758" data-mp="169bfyiub2kug">
                    <span className="font-semibold" data-id="element-7759" data-mp="168jljbcg2t5r">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-7760" data-mp="1185kwed6alhr">
                    <span className="font-semibold" data-id="element-7761" data-mp="1ypch0z585rwf">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-7762" data-mp="1txkxh8khkmfs">
                    <span className="font-semibold" data-id="element-7763" data-mp="1tx2w5xiyvi10">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-7764" data-mp="3t4jc77ftum7f">
                    <span className="font-semibold" data-id="element-7765" data-mp="3t7xaa3hgk992">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-7766" data-mp="3jezpfqozu87a">
                    <span className="font-semibold" data-id="element-7767" data-mp="3jegewv1oam3a">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-7768" data-mp="39i3pwah2owev">
                    <span className="font-semibold" data-id="element-7769" data-mp="39in0f64e8iiv">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-7770" data-mp="30gndjx6p6k80">
                    <span className="font-semibold" data-id="element-7771" data-mp="30i8cdz01opbo">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-7772" data-mp="yqbwkd6ugtb3">
                    <span className="font-semibold" data-id="element-7773" data-mp="yoher7qvdhb8">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-7774" data-mp="2e906e7fmtn99">
                    <span className="font-semibold" data-id="element-7775" data-mp="2e9t30fkvmwdo">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-7776" data-mp="2o6xsopdxl9z8">
                    <span className="font-semibold" data-id="element-7777" data-mp="2o6nzksxlampb">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-7778" data-mp="3205jwfy3o3ge">
                    <span className="font-semibold" data-id="element-7779" data-mp="2wzhsht5ylkln">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-7780" data-mp="3bvqq6qa7bpvy">
                    <span className="font-semibold" data-id="element-7781" data-mp="36xfhwjzk1nfu">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7782" data-mp="1hklhh7k9cn9r">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-7783" data-mp="3mz9357ff6qe">
                  <div data-id="element-7784" data-mp="3m7lagk2mdao">
                    <span className="font-semibold" data-id="element-7785" data-mp="8lk8j2eutmds">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-7786" data-mp="8ndj2ssl8qr2">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-7787" data-mp="hk4nptl7hdx9">
                    <span className="font-semibold" data-id="element-7788" data-mp="3h1cftwndwg5m">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-7789" data-mp="3gz9g2kwfj8np">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-7790" data-mp="3q0r4cqjzqgnh">
                    <span className="font-semibold" data-id="element-7791" data-mp="3usqwvxxqe7a1">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-7792" data-mp="3uuvc3c12yspm">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7793" data-mp="32a3hmz5f1mgv">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-7794" data-mp="2ptlzfvxniw38">
                  <div data-id="element-7795" data-mp="2ls0hvxa69tmp">
                    <span className="font-semibold" data-id="element-7796" data-mp="2lr7hde1t40d0">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-7797" data-mp="2bu2sh6gktqsi">
                    <span className="font-semibold" data-id="element-7798" data-mp="2bucj8w9g41z9">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-7799" data-mp="271jei8dz8tn6">
                    <span className="font-semibold" data-id="element-7800" data-mp="223zyb2xpy6sl">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-7801" data-mp="1y0v2fj8593cx">
                  <div data-id="element-7802" data-mp="1t5o1gj69ecrp">
                    <span className="font-semibold" data-id="element-7803" data-mp="1o4i4ua9ha5cw">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-7804" data-mp="1ej8v2dscmmv8">
                    <span className="font-semibold" data-id="element-7805" data-mp="1jfpljen0tdbh">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-7806" data-mp="14kjecoc6v8gk">
                    <span className="font-semibold" data-id="element-7807" data-mp="19htasnxzwkdw">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-7808" data-mp="1x7w04waizc8t">
                    <span className="font-semibold" data-id="element-7809" data-mp="1x9gr6bxmsd1x">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-7810" data-mp="1ngj9g80gqs4d">
                    <span className="font-semibold" data-id="element-7811" data-mp="1nhareg150rc6">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-7812" data-mp="2g5zv8mtkvarm">
                    <span className="font-semibold" data-id="element-7813" data-mp="2g5geh9fnysf6">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-7814" data-mp="9hptzejiwr59">
                    <span className="font-semibold" data-id="element-7815" data-mp="9gxuvtqpwba8">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-7816" data-mp="j7sudzsz57dm">
                    <span className="font-semibold" data-id="element-7817" data-mp="j8amck8lszfq">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-7818" data-mp="easn5lke1ykx">
                    <span className="font-semibold" data-id="element-7819" data-mp="3mo9dx12pzved">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-7820" data-mp="3hr96omu4wmlo">
                    <span className="font-semibold" data-id="element-7821" data-mp="3hrrcob6lmf6s">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-7822" data-mp="3rm14nrrmpbgz">
                    <span className="font-semibold" data-id="element-7823" data-mp="3rpp0ixkjq9yv">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-7824" data-mp="16wbsjmib83rz">
                    <span className="font-semibold" data-id="element-7825" data-mp="16x3myu067vgo">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-7826" data-mp="35fyqvob55rei">
                    <span className="font-semibold" data-id="element-7827" data-mp="35d2qzufqgsnf">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-7828" data-mp="3f5s1alzjs0h6">
                    <span className="font-semibold" data-id="element-7829" data-mp="3f6ba9d77zej2">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-7830" data-mp="3o4vjlnqw4bum">
                    <span className="font-semibold" data-id="element-7831" data-mp="3o43mu9lk482r">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-7832" data-mp="1xf616uslntq">
                    <span className="font-semibold" data-id="element-7833" data-mp="1xyezy2gt1vm">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-7834" data-mp="23qmj09mxntcg">
                  <span className="font-semibold" data-id="element-7835" data-mp="23ro5rbdba4a0">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-7836" data-mp="kw06w30q5m55">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-7837" data-mp="73kcfqlw2wfu">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-7838" data-mp="71h9y73jvau8">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-7839" data-mp="zrpxkeguung7">
                  <span className="font-semibold" data-id="element-7840" data-mp="zpv94q6dkd7d">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7841" data-mp="pul8o6vmkdvp">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-7842" data-mp="3fjwxmkgv0zsz">
                  <div data-id="element-7843" data-mp="3ajrc7m16o9jc">
                    <span className="font-semibold" data-id="element-7844" data-mp="3aig08yxl3vh5">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-7845" data-mp="wfq4zmq61uta">
                    <span className="font-semibold" data-id="element-7846" data-mp="wfgl8gvpsjw1">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-7847" data-mp="nn6bt1sn8ifu">
                    <span className="font-semibold" data-id="element-7848" data-mp="nllcyzzaqdc6">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-7849" data-mp="dnntepsdt4pw">
                    <span className="font-semibold" data-id="element-7850" data-mp="dn5lux09r42o">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-7851" data-mp="4nilmeja04b7">
                    <span className="font-semibold" data-id="element-7852" data-mp="4o0ohu0g1gs3">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-7853" data-mp="2310ywic8cysl">
                    <span className="font-semibold" data-id="element-7854" data-mp="1z1tc0rfir6j3">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-7855" data-mp="3gywobis4pc5i">
                    <span className="font-semibold" data-id="element-7856" data-mp="3gxc1ygfywziq">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-7857" data-mp="3qq9jaj984ifr">
                    <span className="font-semibold" data-id="element-7858" data-mp="3qr2p9i0cz3wu">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-7859" data-mp="2y1cbja2n2419">
                    <span className="font-semibold" data-id="element-7860" data-mp="2y1vki1ab9i35">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-7861" data-mp="370pls4yl6wn1">
                    <span className="font-semibold" data-id="element-7862" data-mp="3707iwphf5k65">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-7863" data-mp="mb065pq276ta">
                    <span className="font-semibold" data-id="element-7864" data-mp="m9q6iw3k5qkj">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-7865" data-mp="2ncze20nnyzk9">
                    <span className="font-semibold" data-id="element-7866" data-mp="2nc7vbqf60wbe">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-7867" data-mp="2jdqj4sgw0vn4">
                    <span className="font-semibold" data-id="element-7868" data-mp="2xboz22aux04q">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-7869" data-mp="24mhp875yco46">
                    <span className="font-semibold" data-id="element-7870" data-mp="24lyepbimt206">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-7871" data-mp="1zlk87xwoi049">
                    <span className="font-semibold" data-id="element-7872" data-mp="2ec1atkl1uw2q">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-7873" data-mp="29jhuwgyw3our">
                    <span className="font-semibold" data-id="element-7874" data-mp="1lw07syqkley4">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-7875" data-mp="1gwduyqenm0pu">
                    <span className="font-semibold" data-id="element-7876" data-mp="1gv2t4w4rr31d">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-7877" data-mp="16d2ig23lerh">
                    <span className="font-semibold" data-id="element-7878" data-mp="16wbh79rsstd">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7879" data-mp="3nlpbyggo5v09">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-7880" data-mp="k4gy07p2bfat">
                  <div data-id="element-7881" data-mp="k35rw1859vgj">
                    <span className="font-semibold" data-id="element-7882" data-mp="k2cuhqv2sib2">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-7883" data-mp="64ejmvfyr40b">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-7884" data-mp="2qtc0xs3iei03">
                    <span className="font-semibold" data-id="element-7885" data-mp="2qwzwsxwffghz">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-7886" data-mp="2vvktwogdtk27">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-7887" data-mp="36c4zcat0p7bp">
                    <span className="font-semibold" data-id="element-7888" data-mp="36atsu386tlgw">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-7889" data-mp="31ed3j5p95128">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-7890" data-mp="3u0eq7r538l9p">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-7891" data-mp="3l1b6cky3k1u5">
                  <div data-id="element-7892" data-mp="3g2q5qkexng56">
                    <span className="font-semibold" data-id="element-7893" data-mp="3g39epbmluu72">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-7894" data-mp="29kkxeu2agvwu">
                    <span className="font-semibold" data-id="element-7895" data-mp="24nudtwr9zi6o">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-7896" data-mp="k7d2vnw1kkf3">
                    <span className="font-semibold" data-id="element-7897" data-mp="p4mt35r9903z">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-7898" data-mp="t43zq2if43aq">
                  <div data-id="element-7899" data-mp="xyhx2agn4ki1">
                    <span className="font-semibold" data-id="element-7900" data-mp="xyrq66wzf7ry">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-7901" data-mp="17t9y5o9flphk">
                    <span className="font-semibold" data-id="element-7902" data-mp="1cu7mkil1bfri">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-7903" data-mp="1gv9f6gkg4tki">
                    <span className="font-semibold" data-id="element-7904" data-mp="1gtgi9szmxnpp">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-7905" data-mp="3inxj6s4wzl1c">
                    <span className="font-semibold" data-id="element-7906" data-mp="3info3ytzncv0">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-7907" data-mp="kue37at9x4gq">
                    <span className="font-semibold" data-id="element-7908" data-mp="kv5l5ity73oj">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-7909" data-mp="ur8zdn22qsru">
                    <span className="font-semibold" data-id="element-7910" data-mp="uqpim9o5uafe">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-7911" data-mp="13sgjn17r9t2x">
                    <span className="font-semibold" data-id="element-7912" data-mp="13t8h6hkwy0vu">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-7913" data-mp="1ij5we80v7g8w">
                    <span className="font-semibold" data-id="element-7914" data-mp="1dhqc4i6ihdrk">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-7915" data-mp="1riaz7qjglvqv">
                    <span className="font-semibold" data-id="element-7916" data-mp="1miybz4ooemnr">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-7917" data-mp="2659k12nt9rwm">
                    <span className="font-semibold" data-id="element-7918" data-mp="262x6d1p363pd">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-7919" data-mp="1x3a30acsqnto">
                    <span className="font-semibold" data-id="element-7920" data-mp="1x64o843mwco5">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-7921" data-mp="1nd7bkelbphxg">
                    <span className="font-semibold" data-id="element-7922" data-mp="1ncnq4nwgsbeo">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-7923" data-mp="1dhdq25pmme3j">
                    <span className="font-semibold" data-id="element-7924" data-mp="1dhvsxl6snqkf">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-7925" data-mp="14gpbn9wbmbq4">
                    <span className="font-semibold" data-id="element-7926" data-mp="14g62oionexo8">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-7927" data-mp="1owqtdwiprb4k">
                    <span className="font-semibold" data-id="element-7928" data-mp="1ovywmiddr7cp">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-7929" data-mp="1ex9p9jj415ap">
                    <span className="font-semibold" data-id="element-7930" data-mp="1ewq8i6574my9">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-7931" data-mp="15yegq0slfj48">
                  <span className="font-semibold" data-id="element-7932" data-mp="15zfr031sc1kw">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-7933" data-mp="115lcde11ecso">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};